import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LayoutUserHeader from '../components/LayoutUserHeader';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const GetPurchaseInvoice = () => {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedInvoiceData, setSelectedInvoiceData] = useState(null);
  const hasIGST = selectedInvoiceData?.Products?.some(p => parseFloat(p.IGSTAmount) > 0);
  const navigate = useNavigate();
  const invoiceRef = useRef();

  const formatDate = (dateString) => {
  if (!dateString) return '-';
  const d = new Date(dateString);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const convertNumberToWords = (amount) => {
    const numericValue = parseFloat(amount);
    if (isNaN(numericValue)) return "";

    const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const places = ['', 'Thousand', 'Lakh', 'Crore'];

    const getWord = (n) => {
        let word = '';
        if (n > 99) {
            word += units[Math.floor(n / 100)] + ' Hundred ';
            n %= 100;
        }
        if (n > 19) {
            word += tens[Math.floor(n / 10)] + ' ';
            n %= 10;
        } else if (n >= 10) {
            word += teens[n - 10] + ' ';
            n = 0;
        }
        if (n > 0) {
            word += units[n] + ' ';
        }
        return word;
    };

    const numberToIndianWords = (num) => {
        if (num === 0) return 'Zero';

        let str = '';
        let parts = ['', '', '', ''];
        parts[0] = num % 1000; // units
        parts[1] = Math.floor((num % 100000) / 1000); // thousands
        parts[2] = Math.floor((num % 10000000) / 100000); // lakhs
        parts[3] = Math.floor(num / 10000000); // crores

        for (let i = 3; i >= 0; i--) {
            if (parts[i]) {
                str += getWord(parts[i]) + places[i] + ' ';
            }
        }

        return str.trim();
    };

    const [whole, fraction] = numericValue.toFixed(2).split('.');
    let result = numberToIndianWords(Number(whole)).toUpperCase();

    if (Number(fraction) > 0) {
        result += ` AND ${numberToIndianWords(Number(fraction)).toUpperCase()} PAISE`;
    }

    return result + " RUPEES ONLY";
};

 const getDecimalValue = (val) => {
  if (val && typeof val === 'object' && '$numberDecimal' in val) {
    return parseFloat(val.$numberDecimal).toFixed(2);
  }
  const num = parseFloat(val);
  return isNaN(num) ? "0.00" : num.toFixed(2);
};

  const fetchInvoices = async () => {
    try {
      const token = localStorage.getItem('token');
      const gstNumber = localStorage.getItem('gstNumber');

      const res = await axios.get(`${BASE_URL}/PurchaseInvoice/List`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const rawData = res.data.data || [];

      const gstFilteredData = (gstNumber
  ? rawData.filter(item => item.GSTNumber === gstNumber)
  : rawData
).sort((a, b) => new Date(b.createdDateTime) - new Date(a.createdDateTime));

      const formattedData = gstFilteredData.map((item, index) => ({
        ...item,
        id: item._id,
        serialNumber: index + 1,
        InvoiceDate: item.InvoiceDate
          ? new Date(item.InvoiceDate).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })
          : '',
      }));

      setInvoices(formattedData);
      setFilteredInvoices(formattedData);
    } catch (err) {
      console.error('Fetch invoices failed:', err);
      toast.error('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const taxSummary = useMemo(() => {
  return selectedInvoiceData?.Products?.reduce(
    (acc, item) => {
      acc.IGST += parseFloat(item.IGSTAmount) || 0;
      acc.CGST += parseFloat(item.CGSTAmount) || 0;
      acc.SGST += parseFloat(item.SGSTAmount) || 0;
      acc.OtherCharges += parseFloat(item.OtherCharges) || 0;
      acc.Cess += parseFloat(item.Cess) || 0;
      return acc;
    },
    {
      IGST: 0,
      CGST: 0,
      SGST: 0,
      OtherCharges: 0,
      Cess: 0,
    }
  );
}, [selectedInvoiceData]);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearch(value);
    setFilteredInvoices(
      invoices.filter(
        (invoice) =>
          invoice?.InvoiceNumber?.toLowerCase().includes(value) ||
          invoice?.CustomerName?.toLowerCase().includes(value)
      )
    );
  };

  const handleAdd = () => navigate('/AddPurchaseInvoice');

  const handleEdit = (id) => navigate(`/AddPurchaseInvoice/${id}`);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BASE_URL}/PurchaseInvoice/Delete/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Invoice deleted successfully');
      fetchInvoices();
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Delete failed');
    }
  };

  const handleDownload = async (id) => {
    try {
      const token = localStorage.getItem('token');

      const res = await axios.get(`${BASE_URL}/PurchaseInvoice/Get/${id}`);

      const invoiceData = res.data.data;
      if (!invoiceData) {
        toast.error('No data found for invoice');
        return;
      }
      
      console.log(invoiceData);

      setSelectedInvoiceData(invoiceData);

      setTimeout(async () => {
        try {
          const canvas = await html2canvas(invoiceRef.current, {
            scale: 2,
            scrollY: -window.scrollY,
            useCORS: true,
          });

          const imgData = canvas.toDataURL('image/png');
          const pdf = new jsPDF('p', 'pt', 'a4');
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

          pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

          const invoiceNumber = invoiceData?.InvoiceNumber || 'Unknown';
          const consigneeName = invoiceData?.ConsigneeDetails?.Name || 'Customer';

          pdf.save(`Invoice_${invoiceNumber}_${consigneeName}.pdf`);
          setSelectedInvoiceData(null);
        } catch (err) {
          console.error("PDF generation error:", err);
          toast.error("Failed to generate invoice PDF");
        }
      }, 500);
    } catch (error) {
      console.error('Download error:', error.response?.data || error.message || error);
      toast.error('Failed to download invoice');
    }
  };

  const columns = [
  { field: 'serialNumber', headerName: 'S.No', flex: 1, sortable: false },
  { field: 'InvoiceNumber', headerName: 'Invoice Number', flex: 2 },
  { field: 'InvoiceDate', headerName: 'Invoice Date', flex: 2 },
  {
    field: 'actions',
    headerName: 'Actions',
    flex: 3,
    renderCell: (params) => (
      <>
        <Button variant="outlined" size="small" onClick={() => handleEdit(params.row.id)}>
          Edit
        </Button>
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={() => handleDelete(params.row.id)}
          style={{ marginLeft: 8 }}
        >
          Delete
        </Button>
        {/* Show Download button only if SupplierType !== 'Registered' */}
        {params.row.SupplierType !== 'Registered' && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleDownload(params.row.id)}
            style={{ marginLeft: 8 }}
          >
            Download Invoice
          </Button>
        )}
      </>
    ),
  },
];

  return (
    <>
      <LayoutUserHeader />
      <ToastContainer position="top-right" autoClose={3000} />
      <Box p={2}>
        <Typography variant="h5" gutterBottom>
          Purchase Invoice List
        </Typography>
        <Box display="flex" justifyContent="space-between" mb={2}>
          <TextField label="Search" value={search} onChange={handleSearch} />
          <Button variant="contained" color="primary" onClick={handleAdd}>
            Add Invoice
          </Button>
          <Button variant="outlined" color="secondary" onClick={() => navigate('/PurchaseExcelUploader')} >Upload from Excel</Button>
        </Box>
        <DataGrid
          autoHeight
          rows={filteredInvoices}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5, 10]}
          loading={loading}
          getRowId={(row) => row.id}
        />
      </Box>

   {selectedInvoiceData && (
  <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
    <div
      ref={invoiceRef}
      style={{
        width: '794px',
        minHeight: '1123px',
        padding: '30px',
        fontFamily: 'Roboto, sans-serif',
        fontSize: '12px',
        lineHeight: '1.5',
        border: '1px solid #ccc',
        backgroundColor: '#fff',
        boxSizing: 'border-box',
        color: '#000',
      }}
    >
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '15px' }}>
  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
    <p style={{ margin: 2, flex: 1, textAlign: 'left' }}>
      GSTIN: {selectedInvoiceData?.GSTNumber || 'N/A'}
    </p>
    <h2 style={{ margin: 0, flex: 1, textAlign: 'center' }}>
      PURCHASE INVOICE
    </h2>
    <div style={{ flex: 1 }} /> {/* Empty spacer to balance the layout */}
  </div>

  <h3 style={{ margin: 0 }}>
    {selectedInvoiceData?.registrationData.firmName || 'Company Name'}
  </h3>
  <h4 style={{ margin: 0 }}>
    {selectedInvoiceData?.registrationData.address || 'Firm Address'}
  </h4>
  <p style={{ margin: 2 }}>
    Email: {selectedInvoiceData?.CustomerEmail || 'company@gmail.com'}
  </p>
  <p style={{ margin: 2 }}>
    PAN: {selectedInvoiceData?.GSTNumber
      ? selectedInvoiceData.GSTNumber.substring(2, 12)
      : 'N/A'}
  </p>
  <p style={{ margin: 2 }}>
    State: {selectedInvoiceData?.CustomerStateId || 'State'} | Code: {selectedInvoiceData?.CustomerStateCode != null
      ? String(selectedInvoiceData.CustomerStateCode).padStart(2, '0')
      : '--'}
  </p>
</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }} border="1">
  <tbody>
    <tr>
      <td><strong>Invoice No:</strong> {selectedInvoiceData?.InvoiceNumber || '-'}</td>
     <td>
  <strong>Transport Date:</strong>{' '}
  {formatDate(selectedInvoiceData?.TransportDate)}
</td>
      <td>
  <strong>Invoice Date:</strong>{' '}
  {selectedInvoiceData?.InvoiceDate
    ? (() => {
        const d = new Date(selectedInvoiceData.InvoiceDate);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
      })()
    : '-'}
</td>
    </tr>
    <tr>
      <td><strong>Place Of Supply:</strong> {selectedInvoiceData?.PlaceOfSupply || '-'}</td>
      <td><strong>State:</strong> {selectedInvoiceData?.StateId || '-'}</td>
      <td><strong>Vehicle No:</strong> {selectedInvoiceData?.VehicleNumber || '-'}</td>
    </tr>
    <tr>
      <td>
  <strong>State Code:</strong>{' '}
  {selectedInvoiceData?.StateCode != null
    ? String(selectedInvoiceData.StateCode).padStart(2, '0')
    : '-'}
</td>
      <td><strong>Transportation Mode:</strong> {selectedInvoiceData?.TranspotationMode || '-'}</td>
      <td><strong>GR No:</strong> {selectedInvoiceData?.GRNumber || '-'}</td>
    </tr>
    <tr>
      <td><strong>Transporter Name:</strong> {selectedInvoiceData?.TransporterName || '-'}</td>
      <td></td>
      <td></td>
    </tr>
  </tbody>
</table>
<table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }} border="1">
  <tbody>
    <tr>
      <td colSpan={3}><strong>Name:</strong> {selectedInvoiceData?.CustomerName || '-'}</td>
    </tr>
    <tr>
      <td colSpan={3}><strong>Address:</strong> {selectedInvoiceData?.CustomerAddress || '-'}</td>
    </tr>
    <tr>
      <td><strong>GSTIN:</strong> {selectedInvoiceData?.CustomerGSTNumber || '-'}</td>
      <td>
        <strong>State Code:</strong>{' '}
        {selectedInvoiceData?.CustomerStateCode !== undefined
          ? selectedInvoiceData.CustomerStateCode.toString().padStart(2, '0')
          : '-'}
      </td>
      <td><strong>State:</strong> {selectedInvoiceData?.CustomerStateId || '-'}</td>
    </tr>
  </tbody>
</table>
      <table style={{ width: '100%', borderCollapse: 'collapse' }} border="1">
  <thead style={{ backgroundColor: '#d9e7fb' }}>
  <tr style={{ fontSize: '10px' }}>
    <th>S.No</th>
    <th>Product</th>
    <th>HSN/SAC</th>
    <th>Unit</th>
    <th>Quantity</th>
    <th>Rate</th>
    <th>Amount</th>
  </tr>
</thead>
  <tbody>
  {selectedInvoiceData?.Products?.map((item, index) => {
    const amount = parseFloat(item.Amount) || 0;
    const iGSTAmount = parseFloat(item.IGSTAmount) || 0;
    const cGSTAmount = parseFloat(item.CGSTAmount) || 0;
    const sGSTAmount = parseFloat(item.SGSTAmount) || 0;
    const otherCharges = parseFloat(item.OtherCharges) || 0;
    const cess = parseFloat(item.Cess) || 0;
    const discountAmount = parseFloat(item.DiscountAmount) || 0;

    const rowTotal = amount + iGSTAmount + cGSTAmount + sGSTAmount + otherCharges + cess - discountAmount;

    return (
      <tr key={index}>
        <td style={{textAlign: 'center'}}>{index + 1}</td>
        <td style={{textAlign: 'left'}}>{item.ProductName}</td>
        <td style={{textAlign: 'center'}}>{item.HSNCode}</td>
        <td style={{textAlign: 'center'}}>{item.Unit}</td>
        <td style={{textAlign: 'right'}}>{getDecimalValue(item.Quantity)}</td>
        <td style={{textAlign: 'right'}}>{getDecimalValue(item.Rate)}</td>
        <td style={{textAlign: 'right'}}>{getDecimalValue(item.Quantity * item.Rate)}</td>
      </tr>
    );
  })}
</tbody>
</table>
     <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '30px' }}>
  <table
    style={{
      width: '50%',
      fontSize: '10px',
      borderCollapse: 'collapse',
      marginRight: 'auto'
    }}
    border="1"
  >
    <thead style={{ backgroundColor: '#d9e7fb' }}>
      <tr>
        <th style={{ padding: '6px', textAlign: 'left' }}>Description</th>
        <th style={{ padding: '6px', textAlign: 'right' }}>Value</th>
      </tr>
    </thead>
    <tbody>
      {(() => {
        const totalAmount = selectedInvoiceData?.Products?.reduce((acc, item) => {
          const amount = parseFloat(item.Amount) || 0;
          const iGSTAmount = parseFloat(item.IGSTAmount) || 0;
          const cGSTAmount = parseFloat(item.CGSTAmount) || 0;
          const sGSTAmount = parseFloat(item.SGSTAmount) || 0;
          const otherCharges = parseFloat(item.OtherCharges) || 0;
          const cess = parseFloat(item.Cess) || 0;
          const discountAmount = parseFloat(item.DiscountAmount) || 0;
          return acc + (amount + iGSTAmount + cGSTAmount + sGSTAmount + otherCharges + cess - discountAmount);
        }, 0);

        return (
          <>
            <tr>
              <td style={{ padding: '6px' }}>Total Invoice Amount</td>
              <td style={{ padding: '6px', textAlign: 'right', fontWeight: 'bold' }}>₹ {totalAmount.toFixed(2)}</td>
            </tr>
            <tr>
              <td style={{ padding: '6px' }}>Amount in Words</td>
              <td style={{ padding: '6px', textAlign: 'right', fontWeight: 'bold' }}>{convertNumberToWords(totalAmount)}</td>
            </tr>
          </>
        );
      })()}
    </tbody>
  </table>
  
  {/* Right Table: Tax Summary */}
  <table
    style={{
      width: '45%',
      fontSize: '10px',
      borderCollapse: 'collapse',
      marginLeft: 'auto'
    }}
    border="1"
  >
    <thead style={{ backgroundColor: '#d9e7fb' }}>
      {/* <tr>
        <th style={{ padding: '6px', textAlign: 'left' }}>Taxable Amount</th>
        <th style={{ padding: '6px', textAlign: 'right' }}> Amount</th>
      </tr> */}
    </thead>
    <tbody>
  {(() => {
    const totalTaxableValue = selectedInvoiceData?.Products?.reduce((acc, item) => {
      const qty = parseFloat(item.Quantity) || 0;
      const rate = parseFloat(item.Rate) || 0;
      return acc + qty * rate;
    }, 0);

    return (
      <tr>
        <td style={{ padding: '6px',backgroundColor: '#d9e7fb',fontWeight: 'bold' }}>Taxable Amount</td>
        <td style={{ padding: '6px', textAlign: 'right',backgroundColor: '#d9e7fb',fontWeight: 'bold' }}>{getDecimalValue(totalTaxableValue)}</td>
      </tr>
    );
  })()}

  <tr>
    <td style={{ padding: '6px' }}>IGST</td>
    <td style={{ padding: '6px', textAlign: 'right' }}>{getDecimalValue(taxSummary.IGST)}</td>
  </tr>
  <tr>
    <td style={{ padding: '6px' }}>CGST</td>
    <td style={{ padding: '6px', textAlign: 'right' }}>{getDecimalValue(taxSummary.CGST)}</td>
  </tr>
  <tr>
    <td style={{ padding: '6px' }}>SGST</td>
    <td style={{ padding: '6px', textAlign: 'right' }}>{getDecimalValue(taxSummary.SGST)}</td>
  </tr>
  <tr>
    <td style={{ padding: '6px' }}>Other Charges</td>
    <td style={{ padding: '6px', textAlign: 'right' }}>{getDecimalValue(taxSummary.OtherCharges)}</td>
  </tr>
  <tr>
    <td style={{ padding: '6px' }}>Cess</td>
    <td style={{ padding: '6px', textAlign: 'right' }}>{getDecimalValue(taxSummary.Cess)}</td>
  </tr>
</tbody>
  </table>
</div>
  <div style={{
    display: 'flex',
    border: '1px solid #ccc',
    marginTop: '20px',
    borderRadius: '4px',
    overflow: 'hidden'
  }}>
    {/* Terms & Conditions */}
    <div style={{ width: '50%', padding: '12px', borderRight: '1px solid #ccc' }}>
      <p style={{ margin: '4px 0' }}>
       Certified that particulars given above are true and correct
      </p>
      <p style={{ margin: '4px 0' }}>
        Subject to {selectedInvoiceData?.registrationData.district || '-'} jurisdiction only.
      </p>
    </div>

    {/* Authorized Signatory */}
    <div style={{ flex: 1, padding: '15px' }}>
      <div style={{marginTop: '10px' }}>
       For, {selectedInvoiceData?.registrationData.firmName || 'Company Name'}
      </div>
    <div style={{ marginTop: '40px', textAlign: 'center' }}>
     Authorised Signatory
    </div>

    </div>
  </div>
  {/* ✅ New section ends here */}
    </div>
  </div>

)}


    </>
  );
};

export default GetPurchaseInvoice;
